import * as React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  AutocompleteRenderGetTagProps,
  createFilterOptions
} from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";
import { useGetAppointmentTypesQuery } from "services/appointment-types";

type SelectAppointmentTypeProps = {
  label?: string;
  onOptionSelected?: (option: { label: string; value: string } | null) => void;
  isActive?: boolean | null;
  required?: boolean;
  multiple?: boolean;
  type?: string;
  sx?: SxProps;
  /**
   * Auto-focus the input, auto-open the dropdown on focus, and pre-highlight an
   * option. Default `true` (existing behavior). Pass `false` where the field is
   * pre-filled and should stay closed on mount — e.g. the scribe template modal,
   * where an auto-open dropdown over a still-settling form let stray clicks/Enter
   * commit the wrong appointment type (SN-19065).
   */
  autoFocus?: boolean;
  openOnFocus?: boolean;
  autoHighlight?: boolean;
};

type AppointmentTypeOption = { label: string; value: string };

// Five rows of default chips (32px + 3px margin above and below). Past that the chips scroll
// inside the field, so a long selection never pushes the option list off the screen (SN-20037).
const SELECTED_TYPES_MAX_HEIGHT = 5 * 38;

const renderScrollingTags = (
  selected: AppointmentTypeOption[],
  getTagProps: AutocompleteRenderGetTagProps
) => (
  <Box
    data-testid="selected-appointment-types"
    sx={{
      display: "flex",
      flexWrap: "wrap",
      width: "100%",
      maxHeight: SELECTED_TYPES_MAX_HEIGHT,
      overflowY: "auto"
    }}
  >
    {selected.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      return <Chip key={key} {...tagProps} label={option.label} />;
    })}
  </Box>
);

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export type RhfSelectAppointmentType<T extends FieldValues> =
  SelectAppointmentTypeProps & UseControllerProps<T>;

export default function SelectAppointmentType<T extends FieldValues>({
  control,
  label = "By Type",
  name,
  onOptionSelected,
  isActive = null,
  required = false,
  multiple = false,
  type,
  sx = {},
  autoFocus = true,
  openOnFocus = true,
  autoHighlight = true
}: RhfSelectAppointmentType<T>) {
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: { label: string; value: string }) => option.label
  });

  const { data: types = [], isLoading } = useGetAppointmentTypesQuery({
    is_active: isActive,
    type: type
  });

  return (
    <AutocompleteFormField
      openOnFocus={openOnFocus}
      autoHighlight={autoHighlight}
      autoFocus={autoFocus}
      name={name}
      label={label}
      isLoading={isLoading}
      required={required}
      multiple={multiple}
      filterOptions={filterOptions}
      options={types.map((appointmentType) => ({
        label: `${appointmentType.name}${
          !appointmentType.is_active ? " (disabled)" : ""
        }`,
        value: String(appointmentType.id)
      }))}
      getOptionLabel={(option) => {
        return option?.label ?? "";
      }}
      renderOption={(props: object, option: any, { selected }: any) => (
        <ListItem {...props}>
          {multiple && (
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
          )}
          <ListItemText>{option?.label}</ListItemText>
        </ListItem>
      )}
      renderTags={multiple ? renderScrollingTags : undefined}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onOptionSelected={(option) => {
        if (onOptionSelected) {
          onOptionSelected(option);
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 220, ...sx }}
    />
  );
}
